import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from '../axios'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref([])
  const unreadCount = ref(0)
  const loading = ref(false)
  
  // Récupérer toutes les notifications
  const fetchNotifications = async (unreadOnly = false) => {
    loading.value = true
    try {
      const response = await axios.get('/api/v1/notifications', {
        params: { unread_only: unreadOnly },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      notifications.value = response.data
      return response.data
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error)
      return []
    } finally {
      loading.value = false
    }
  }
  
  // Récupérer le nombre de notifications non lues
  const fetchUnreadCount = async () => {
    try {
      // Appeler un endpoint spécifique pour obtenir uniquement le nombre
      const response = await axios.get('/api/v1/notifications/unread-count', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      unreadCount.value = response.data.count || 0
      return unreadCount.value
    } catch (error) {
      // Si l'endpoint spécifique n'existe pas, utiliser la méthode précédente
      try {
        const unreadNotifications = await fetchNotifications(true)
        unreadCount.value = unreadNotifications.length
        return unreadCount.value
      } catch (innerError) {
        console.error('Erreur lors de la récupération du nombre de notifications non lues:', innerError)
        return 0
      }
    }
  }
  
  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    try {
      const response = await axios.put(`/api/v1/notifications/${notificationId}/read`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      // Mettre à jour la notification dans la liste
      const index = notifications.value.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        notifications.value[index] = response.data
        if (unreadCount.value > 0) unreadCount.value--
      }
      
      return response.data
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error)
      return null
    }
  }
  
  // Marquer toutes les notifications comme lues
  const markAllAsRead = async () => {
    try {
      const response = await axios.put('/api/v1/notifications/mark-all-read', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      // Mettre à jour toutes les notifications dans la liste
      notifications.value = notifications.value.map(n => ({ ...n, is_read: true }))
      unreadCount.value = 0
      
      return response.data
    } catch (error) {
      console.error('Erreur lors du marquage de toutes les notifications comme lues:', error)
      return null
    }
  }
  
  // Supprimer une notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/api/v1/notifications/${notificationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      // Supprimer la notification de la liste
      const index = notifications.value.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        const wasUnread = !notifications.value[index].is_read
        notifications.value.splice(index, 1)
        if (wasUnread && unreadCount.value > 0) unreadCount.value--
      }
      
      return true
    } catch (error) {
      console.error('Erreur lors de la suppression de la notification:', error)
      return false
    }
  }
  
  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  }
})
